import React, { useState, useCallback } from 'react';
import fetchWithAuth from '../../api/FetchInstance';
import PredioForm from './PredioComponentes/informacion_general';
import PredioDetail from './PredioDetalle';
import FloatingAddButton from '../../components/FloatingAddButton';

import {
  Box,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
} from '@chakra-ui/react';

// Importar componentes optimizados
import {
  VirtualizedContainer,
  GridCard
} from '../../components/VirtualizedGrid';
import { InfiniteScrollContainer } from '../../components/InfiniteScrollContainer';
import { SearchFiltersWithProgressiveSearch } from '../../components/SearchFiltersWithProgressiveSearch';

// Importar hook híbrido
import { useInfiniteSearchGrid } from '../../hooks/useInfiniteSearchGrid';
import { useVirtualizedGrid } from '../../hooks/useVirtualizedGrid';

// Importar configuración específica de predios
import {
  prediosGridConfig,
  prediosFilterFields,
  renderPredioCard
} from './PrediosVirtualizedConfig';

const PredialTesoreriaVirtualized: React.FC = () => {
  const [selectedPredioId, setSelectedPredioId] = useState<string | null>(null);
  const toast = useToast();

  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  const {
    isOpen: isModalOpenx,
    onOpen: onModalOpenx,
    onClose: onModalClosex,
  } = useDisclosure();

  // Función de fetch optimizada
  const fetchPredios = useCallback(async (page: number, pageSize: number, search: string) => {
    try {
      const response = await fetchWithAuth(
        `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/predio?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching predios:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    }
  }, [toast]);

  // Usar el hook híbrido para búsqueda + carga infinita
  const {
    visibleItems: searchItems,
    loading,
    loadingMore,
    hasMore,
    isSearchMode,
    searchTerm,
    totalFiltered: searchTotalFiltered,
    totalPredios,
    hasActiveFilters: searchHasActiveFilters,
    loadMore,
    handleSearch,
    clearSearch,
    refreshData
  } = useInfiniteSearchGrid({
    fetchFunction: fetchPredios,
    initialPageSize: 20,
    searchPageSize: 100,
    searchDebounceMs: 300
  });

  // Usar el hook original para filtros locales (solo cuando no hay búsqueda)
  const {
    visibleItems: filteredItems,
    totalFiltered,
    filters,
    hasActiveFilters: hasLocalFilters,
    updateFilter,
    clearFilters: clearLocalFilters
  } = useVirtualizedGrid(searchItems, prediosGridConfig);

  // Combinar estados
  const visibleItems = (!isSearchMode && hasLocalFilters) ? filteredItems : searchItems;
  const hasActiveFilters = searchHasActiveFilters || (!isSearchMode && hasLocalFilters);
  const clearFilters = () => {
    clearSearch();
    clearLocalFilters();
  };

  // Manejadores
  const handleRowClick = (id: string) => {
    setSelectedPredioId(id);
    onModalOpen();
  };

  const closeModal = () => {
    onModalClose();
    setSelectedPredioId(null);
  };

  return (
    <>
      <Box p={4} pt={50} mt={8} width="100%">
        
        {/* Búsqueda progresiva y filtros unificados */}
        <SearchFiltersWithProgressiveSearch
          searchTerm={searchTerm}
          isSearching={loading && !!searchTerm}
          onSearchChange={handleSearch}
          onClearSearch={clearSearch}
          filters={filters}
          filterFields={prediosFilterFields}
          hasLocalFilters={hasLocalFilters}
          onFilterChange={updateFilter}
          onClearLocalFilters={clearLocalFilters}
          totalResults={isSearchMode ? searchTotalFiltered : (hasLocalFilters ? totalFiltered : totalPredios)}
          hasActiveFilters={hasActiveFilters}
          title="Búsqueda y Filtros de Predios"
        />

        {/* Grid virtualizado con scroll infinito */}
        <InfiniteScrollContainer
          hasMore={hasMore && (!isSearchMode || isSearchMode) && (!hasLocalFilters || isSearchMode)}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
        >
          <VirtualizedContainer
            isLoading={loading}
            isEmpty={visibleItems.length === 0 && !loading}
            emptyMessage="No se encontraron predios"
            emptySubMessage={
              isSearchMode
                ? 'Intenta con otros términos de búsqueda'
                : hasLocalFilters
                ? 'Intenta ajustar los filtros locales'
                : 'No hay predios registrados'
            }
            columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
            spacing={4}
            minItemWidth="380px"
            maxItemWidth="500px"
            minItemHeight="300px"
          >
            {visibleItems.map((predio, index) => (
              <GridCard
                key={`${predio.id}-${index}`}
                item={predio}
                index={index}
                renderContent={(item) => renderPredioCard(item, index, handleRowClick)}
                onClick={() => handleRowClick(predio.id)}
                minWidth="380px"
                maxWidth="500px"
                minHeight="300px"
              />
            ))}
          </VirtualizedContainer>
        </InfiniteScrollContainer>
      </Box>
      
      {/* Botón flotante */}
      <FloatingAddButton 
        text="Nuevo Predio" 
        onClick={onModalOpenx} 
      />
      
      {/* Modal nuevo predio */}
      <Modal isOpen={isModalOpenx} onClose={onModalClosex} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            bgImage="url('/img/bg.webp')"
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            color="white"
            textShadow="2px 2px 4px rgba(0,0,0,0.7)"
            fontWeight="bold"
            position="relative"
          >
            Nuevo Predio
          </ModalHeader>
          <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
          <ModalBody>
            <PredioForm />
          </ModalBody>
          <ModalFooter>
            <Button bg="#5b182b" color="white" _hover={{ bg: "#691f30" }} onClick={onModalClosex}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal detalles del predio */}
      <Modal isOpen={isModalOpen} onClose={closeModal} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            bgImage="url('/img/bg.webp')"
            bgSize="cover"
            bgPosition="center"
            bgRepeat="no-repeat"
            color="white"
            textShadow="2px 2px 4px rgba(0,0,0,0.7)"
            fontWeight="bold"
            position="relative"
          >
            Detalles del Predio
          </ModalHeader>
          <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
          <ModalBody>
            {selectedPredioId ? (
              <PredioDetail id={selectedPredioId} />
            ) : (
              <Box>No se encontraron detalles para este predio.</Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button bg="#5b182b" color="white" _hover={{ bg: "#691f30" }} onClick={closeModal}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PredialTesoreriaVirtualized;